// Run history for one benchmark. Every "Start benchmark" click creates its
// own BenchmarkRun row (see apps/api's benchmark.service.ts), so this page is
// where that history is actually visible/prunable — the list page
// (../index.tsx) only ever shows the current/latest run.
import { useClient } from '@cortex/react-query';
import type {
  Benchmark,
  BenchmarkRun,
  ClassicalStack,
  OperatorStrategyAnalysis,
} from '@cortex/types';
import {
  defaultStack,
  OperatorStrategyAnalysisSchema,
  OperatorStrategyErrorSchema,
  stackDisplayName,
} from '@cortex/types';
import { fork } from '@cortex/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Fragment, useMemo, useState, type CSSProperties } from 'react';

import {
  BenchmarkResults,
  formatKitMetricsSummary,
  KitMetricsPanel,
  statusBadge,
} from '../-benchmark-results';
import {
  BenchKpiCards,
  RunDownloadButton,
  SectionLabel,
  benchTdStyle,
  benchThStyle,
  formatRunCost,
  passRateColor,
  scoreRun,
  type IBenchKpiCard,
} from '../-benchmark-ui';
import { RunReplayModal } from '../../../../components/benchmarks/RunReplayModal';
import { BackButton } from '../../../../components/common/BackButton';
import { ConfirmDialog } from '../../../../components/common/ConfirmDialog';
import { EmptyState } from '../../../../components/common/EmptyState';
import { PageHeader } from '../../../../components/common/PageHeader';
import { Spinner } from '../../../../components/common/Spinner';
import { routes } from '../../../../lib/constants';
import { formatDateTime, formatDuration } from '../../../../lib/format';

import {
  buildClassicalStartBody,
  classicalArmPresets,
  classicalStartConfirmDetail,
} from './-classical-start';
import { ReliabilitySection } from './-reliability';
import {
  runAgentsLabel,
  runAgentsTitle,
  runAgentsUsed,
  runModelLabel,
  runModelTitle,
} from './-run-display';

export const Route = createFileRoute('/agents/orchestration/benchmarks/$benchmarkId')({
  component: BenchmarkRunsPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

const truncate: CSSProperties = {
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  verticalAlign: 'bottom',
};

interface ITrendPoint {
  startedAt: Date;
  /** Scored-event pass fraction (verdicts correct ÷ events with a verdict;
   *  expired events count as failures). */
  passRate: number;
  /** Scenario aggregate rows, when the driver produced them (sim runs). */
  resolvedRate: number | null;
  verdictAccuracy: number | null;
  meanResponseSec: number | null;
  meanFirstTouchSec: number | null;
  eventsExpired: number | null;
  costUsd: number | null;
  turns: number | null;
  maxBlindWindowSec: number | null;
  attentionDecayRatio: number | null;
  /**
   * Agents that participated. Prefers `agents_used` (spawn-tree definitions)
   * then falls back to legacy `agents_launched`.
   */
  agentsLaunched: number | null;
  /** Strategy-judge doctrine ratings (PR #965), keyed by axis. */
  doctrine: Record<DoctrineAxisKey, number | null>;
}

/** Doctrine axes in canonical display order, with human labels. */
export const doctrineAxes = [
  { key: 'prioritization', label: 'Prioritization' },
  { key: 'preStaging', label: 'Pre-staging' },
  { key: 'parallelism', label: 'Parallelism' },
  { key: 'recovery', label: 'Recovery' },
  { key: 'toolDiscipline', label: 'Tool discipline' },
] as const;
export type DoctrineAxisKey = (typeof doctrineAxes)[number]['key'];

function metricValue(results: BenchmarkRun['results'], key: string): number | null {
  const row = results.find((result) => result.key === key);
  return row && typeof row.value === 'number' && Number.isFinite(row.value) ? row.value : null;
}

/** Chronological (oldest→newest) trend points for runs with at least one scored row. */
export function buildTrendPoints(runs: BenchmarkRun[]): ITrendPoint[] {
  return [...runs]
    .map((run) => {
      // Verdict rows only — informational metric/cost rows (passed: null)
      // diluted the rate toward 0% (1/16 instead of 1/5).
      const scorable = run.results.filter((result) => result.passed !== null);
      return {
        startedAt: run.startedAt,
        scorable: scorable.length,
        passed: scorable.filter((result) => result.passed === true).length,
        resolvedRate: metricValue(run.results, 'resolved_rate'),
        verdictAccuracy: metricValue(run.results, 'verdict_accuracy'),
        meanResponseSec: metricValue(run.results, 'mean_response_sec'),
        meanFirstTouchSec: metricValue(run.results, 'mean_first_agent_touch_sec'),
        eventsExpired: metricValue(run.results, 'events_expired'),
        costUsd: metricValue(run.results, 'cost_usd_total'),
        turns: metricValue(run.results, 'turns_total'),
        maxBlindWindowSec: metricValue(run.results, 'max_blind_window_sec'),
        attentionDecayRatio: metricValue(run.results, 'attention_decay_ratio'),
        agentsLaunched:
          metricValue(run.results, 'agents_used') ?? metricValue(run.results, 'agents_launched'),
        doctrine: Object.fromEntries(
          doctrineAxes.map((axis) => [axis.key, metricValue(run.results, `strategy_${axis.key}`)]),
        ) as Record<DoctrineAxisKey, number | null>,
      };
    })
    .filter((point) => point.scorable > 0)
    .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
    .map((point) => ({
      startedAt: point.startedAt,
      passRate: point.passed / point.scorable,
      resolvedRate: point.resolvedRate,
      verdictAccuracy: point.verdictAccuracy,
      meanResponseSec: point.meanResponseSec,
      meanFirstTouchSec: point.meanFirstTouchSec,
      eventsExpired: point.eventsExpired,
      costUsd: point.costUsd,
      turns: point.turns,
      maxBlindWindowSec: point.maxBlindWindowSec,
      attentionDecayRatio: point.attentionDecayRatio,
      agentsLaunched: point.agentsLaunched,
      doctrine: point.doctrine,
    }));
}

/** Parse a run's persisted strategyAnalysis column (analysis, recorded judge
 *  failure, or null when absent/unrecognized). */
export function parseStrategyAnalysis(
  value: unknown,
):
  | { kind: 'analysis'; analysis: OperatorStrategyAnalysis }
  | { kind: 'error'; message: string }
  | null {
  if (value == null) return null;
  const analysis = OperatorStrategyAnalysisSchema.safeParse(value);
  if (analysis.success) return { kind: 'analysis', analysis: analysis.data };
  const error = OperatorStrategyErrorSchema.safeParse(value);
  if (error.success) return { kind: 'error', message: error.data.error };
  return null;
}

/** Muted provenance line for a shift report. Truncation and missing
 *  evidence suffixes tell the reader what the judge could actually see:
 *  a reasoning review written blind to the operator's own words is a
 *  weaker artifact than one written with them. Segment order is model/date,
 *  truncated, reasoning, notes — keep it stable so tests can pin the string. */
export function formatStrategyProvenance(analysis: OperatorStrategyAnalysis): string {
  const truncated = analysis.digestStats.truncated ? ' · digest truncated' : '';
  const noReasoning = analysis.digestStats.reasoningIncluded ? '' : ' · no reasoning';
  const noNotes = analysis.digestStats.notesIncluded ? '' : ' · no notes';
  return `judged by ${analysis.judgeModelId} · ${formatDateTime(analysis.generatedAt)}${truncated}${noReasoning}${noNotes}`;
}

/** Kit badge variant for a 1–5 doctrine rating. */
export function doctrineBadgeClass(rating: number): string {
  if (rating >= 4) return 'badge badge--success';
  if (rating === 3) return 'badge badge--info';
  if (rating === 2) return 'badge badge--warning';
  return 'badge badge--danger';
}

/** Distinct experiment arms among these runs, in first-appearance order. */
export function distinctArmIds(runs: BenchmarkRun[]): string[] {
  const seen = new Set<string>();
  for (const run of runs) {
    if (run.armId) seen.add(run.armId);
  }
  return [...seen];
}

/** Distinct stacks that ran (legacy rows read as the cortex default). */
export function distinctStacks(runs: BenchmarkRun[]): string[] {
  const seen = new Set<string>();
  for (const run of runs) seen.add(run.stack ?? defaultStack);
  return [...seen];
}

/** The one-word coordination shape a run recorded (topology lane), if any. */
export function runTopologyShape(run: Pick<BenchmarkRun, 'results'>): string | null {
  const row = run.results.find((r) => r.key === 'orchestration_topology');
  return typeof row?.value === 'string' ? row.value : null;
}

/** Compact arm chip text: "cortex-sonnet-5-v2 · t3"; null when unarmed. */
export function armBadgeLabel(run: Pick<BenchmarkRun, 'armId' | 'trial'>): string | null {
  if (!run.armId) return null;
  return run.trial != null ? `${run.armId} · t${run.trial}` : run.armId;
}

/**
 * Orchestration Analyze-mode search params covering this trial's window
 * (startedAt → finishedAt, falling back to lastPolledAt while still running).
 * Null when no closed window can be inferred yet — parseOrchestrationSearch
 * would drop a half-specified or inverted range anyway.
 */
export function runAnalyzeSearch(
  run: Pick<BenchmarkRun, 'orchestrationGroupId' | 'startedAt' | 'finishedAt' | 'lastPolledAt'>,
): { orchestrationGroupId: string; analyzeSince: string; analyzeUntil: string } | null {
  const since = new Date(run.startedAt).getTime();
  const untilRaw = run.finishedAt ?? run.lastPolledAt;
  if (untilRaw == null) return null;
  const until = new Date(untilRaw).getTime();
  if (!Number.isFinite(since) || !Number.isFinite(until) || until <= since) return null;
  return {
    orchestrationGroupId: run.orchestrationGroupId,
    analyzeSince: new Date(since).toISOString(),
    analyzeUntil: new Date(until).toISOString(),
  };
}

/** One-line "what actually ran" summary from the run's resolved profile. */
export function formatResolvedProfileSummary(
  profile: NonNullable<BenchmarkRun['resolvedProfile']>,
): string {
  const p = profile.profile;
  const parts: string[] = [];
  parts.push(p.modelConfigId ? `model ${p.modelConfigId}` : 'baseline model');
  if (p.toolRefs) parts.push(`toolRefs override ×${p.toolRefs.length}`);
  // DEV-17197: negative tools-axis arm. Sits immediately after the positive
  // toolRefs override so the line reads "what was added, then what was
  // subtracted" without restating the whole allowlist.
  if (p.disabledTools?.length) parts.push(`tools disabled ×${p.disabledTools.length}`);
  if (p.instructions) parts.push('charter override');
  if (p.memoryMode) parts.push(`memory ${p.memoryMode}`);
  // DEV-17254: the namespace IS the corpus identity of a LIVE arm — shown in
  // full so an operator can paste it into "Continue namespace" on a later
  // Start (the learning-curve arm).
  if (profile.memoryNamespaceId) parts.push(`namespace ${profile.memoryNamespaceId}`);
  const defs = profile.baseline.length;
  parts.push(`baseline ${defs} definition${defs === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * Humanize a canonical disabledTools id (DEV-17197). Split on the first `/`
 * only: `conn/tool` → tool name + connection qualifier; a bare ref (no slash)
 * dropped the whole MCP connection or platform tool from the effective
 * toolRefs.
 */
export function formatDisabledToolId(id: string): { label: string; qualifier: string } {
  const slash = id.indexOf('/');
  if (slash < 0) return { label: id, qualifier: 'whole ref' };
  return { label: id.slice(slash + 1), qualifier: id.slice(0, slash) };
}

interface ITrendSeries {
  key: string;
  label: string;
  color: string;
  /** One value per trend point; null = missing for that run. */
  values: Array<number | null>;
}

interface ITrendPanelSpec {
  title: string;
  /** Fixed domain for 0–1 rates; omit for auto domain from the series. */
  domain?: { min: number; max: number };
  formatY: (v: number) => string;
  series: ITrendSeries[];
}

function hasSeriesData(series: ITrendSeries[]): boolean {
  return series.some((s) => s.values.some((v) => v !== null));
}
function seriesDomain(series: ITrendSeries[]): { min: number; max: number } {
  const nums = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  if (nums.length === 0) return { min: 0, max: 1 };
  const min = Math.min(0, ...nums);
  const max = Math.max(...nums);
  // Flat series (all zeros, or one constant) still need a non-zero span.
  return { min, max: max > min ? max : min + 1 };
}
/** One multi-series SVG panel. Domain is 0–1 for rates, auto for latency/cost. */
function TrendPanel({
  title,
  points,
  domain,
  formatY,
  series,
}: {
  title: string;
  points: ITrendPoint[];
  domain?: { min: number; max: number };
  formatY: (v: number) => string;
  series: ITrendSeries[];
}) {
  if (points.length < 2 || !hasSeriesData(series)) return null;

  const width = 360;
  const height = 100;
  // Bottom pad for the x-axis run ticks (no date labels — just one mark per run).
  const pad = { top: 8, right: 8, bottom: 14, left: 36 };
  const chartWidth = width - pad.left - pad.right;
  const chartHeight = height - pad.top - pad.bottom;
  const { min, max } = domain ?? seriesDomain(series);
  const span = max - min || 1;

  const xScale = (i: number) => pad.left + (i / (points.length - 1)) * chartWidth;
  const yScale = (v: number) => pad.top + (1 - (v - min) / span) * chartHeight;

  const mid = min + span / 2;
  const liveSeries = series.filter((s) => s.values.some((v) => v !== null));

  return (
    <div style={{ minWidth: 0 }}>
      <div className="t-sm t-secondary" style={{ marginBottom: 4 }}>
        {title}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        style={{ overflow: 'visible', display: 'block' }}
      >
        <line
          x1={pad.left}
          y1={pad.top}
          x2={pad.left}
          y2={pad.top + chartHeight}
          stroke="var(--border-subtle)"
          strokeWidth={1}
        />
        <line
          x1={pad.left}
          y1={pad.top + chartHeight}
          x2={pad.left + chartWidth}
          y2={pad.top + chartHeight}
          stroke="var(--border-subtle)"
          strokeWidth={1}
        />
        <line
          x1={pad.left}
          y1={pad.top + chartHeight / 2}
          x2={pad.left + chartWidth}
          y2={pad.top + chartHeight / 2}
          stroke="var(--border-subtle)"
          strokeWidth={0.5}
          strokeDasharray="4,4"
        />
        {[
          { v: min, y: pad.top + chartHeight + 3 },
          { v: mid, y: pad.top + chartHeight / 2 + 3 },
          { v: max, y: pad.top + 4 },
        ].map((tick) => (
          <text
            key={`y-${tick.v}`}
            x={pad.left - 4}
            y={tick.y}
            fill="var(--text-muted)"
            fontSize={8}
            textAnchor="end"
            fontFamily="var(--font-mono)"
          >
            {formatY(tick.v)}
          </text>
        ))}
        {/* X-axis: one tick per run — no date labels (history table has those). */}
        {points.map((p, i) => (
          <line
            key={`x-tick-${i}`}
            x1={xScale(i)}
            y1={pad.top + chartHeight}
            x2={xScale(i)}
            y2={pad.top + chartHeight + 5}
            stroke="var(--border-subtle)"
            strokeWidth={1}
          >
            <title>{formatDateTime(p.startedAt)}</title>
          </line>
        ))}
        {liveSeries.map((s) => {
          // Drop gaps so a missing mid-run doesn't invent a segment across nulls.
          const segments: string[] = [];
          let current: string[] = [];
          s.values.forEach((v, i) => {
            if (v === null) {
              if (current.length > 0) {
                segments.push(current.join(' '));
                current = [];
              }
              return;
            }
            current.push(`${xScale(i)},${yScale(v)}`);
          });
          if (current.length > 0) segments.push(current.join(' '));
          return segments.map((pointsAttr, segIdx) => (
            <polyline
              key={`${s.key}-${segIdx}`}
              points={pointsAttr}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ));
        })}
        {liveSeries.map((s) =>
          s.values.map((v, i) => {
            const point = points[i];
            if (v === null || !point) return null;
            return (
              <circle key={`${s.key}-pt-${i}`} cx={xScale(i)} cy={yScale(v)} r={3} fill={s.color}>
                <title>{`${formatDateTime(point.startedAt)} — ${s.label}: ${formatY(v)}`}</title>
              </circle>
            );
          }),
        )}
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', marginTop: 2 }}>
        {liveSeries.map((s) => (
          <span key={s.key} className="t-sm t-secondary">
            <span style={{ color: s.color }}>━</span> {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
/** The strategy judge's persisted "shift report" for one run. */
function ShiftReport({ value }: { value: unknown }) {
  const parsed = parseStrategyAnalysis(value);
  if (!parsed) return null;
  if (parsed.kind === 'error') {
    return (
      <p className="t-sm t-muted" style={{ margin: '8px 0 0' }}>
        strategy judge failed: {parsed.message}
      </p>
    );
  }
  const { analysis } = parsed;
  const reasoningGroups = [
    { title: 'Figured out', items: analysis.reasoningReview.figuredOut },
    { title: 'Surprised us', items: analysis.reasoningReview.surprises },
    { title: 'Reasoned poorly about', items: analysis.reasoningReview.reasoningGaps },
    { title: 'Learned about the environment', items: analysis.reasoningReview.memoryUse },
  ].filter((group) => group.items.length > 0);
  const lists: Array<{ title: string; items: string[] }> = [
    { title: 'Strengths', items: analysis.strengths },
    { title: 'Weaknesses', items: analysis.weaknesses },
    { title: 'Failure root causes', items: analysis.failureRootCauses },
  ].filter((group) => group.items.length > 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      <strong className="t-sm">Shift report</strong>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
        {doctrineAxes.map((axis) => {
          const entry = analysis.doctrine[axis.key];
          return (
            <span key={axis.key} className="t-sm" title={entry.evidence}>
              {axis.label}{' '}
              <span className={doctrineBadgeClass(entry.rating)}>{entry.rating}/5</span>
            </span>
          );
        })}
      </div>
      <p className="t-sm" style={{ margin: 0, maxWidth: '70ch' }}>
        {analysis.narrative}
      </p>
      {reasoningGroups.length > 0 && (
        <div>
          <span className="t-xs t-secondary">Reasoning review</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-4)', marginTop: 4 }}>
            {reasoningGroups.map((group) => (
              <div key={group.title} style={{ minWidth: 180, flex: '1 1 180px' }}>
                <span className="t-xs t-secondary">{group.title}</span>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {group.items.map((item) => (
                    <li key={item} className="t-xs">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
      {analysis.decisionTimeline.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {analysis.decisionTimeline.map((decision) => (
            <li key={`${decision.tSec}-${decision.decision.slice(0, 24)}`} className="t-xs">
              <span style={{ fontFamily: 'var(--font-mono)' }}>t+{Math.round(decision.tSec)}s</span>
              {' · '}
              {decision.decision} <span className="t-muted">— {decision.assessment}</span>
            </li>
          ))}
        </ul>
      )}
      {lists.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-4)' }}>
          {lists.map((group) => (
            <div key={group.title} style={{ minWidth: 180, flex: '1 1 180px' }}>
              <span className="t-xs t-secondary">{group.title}</span>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {group.items.map((item) => (
                  <li key={item} className="t-xs">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {analysis.experimentSuggestions.length > 0 && (
        <div>
          <span className="t-xs t-secondary">Try next</span>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {analysis.experimentSuggestions.map((idea) => (
              <li key={idea.suggestion} className="t-xs">
                {idea.suggestion} <span className="t-muted">— {idea.rationale}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <span className="t-xs t-muted">{formatStrategyProvenance(analysis)}</span>
    </div>
  );
}

/** Three compact side-by-side panels (quality / latency / cost) so units stay
 *  honest and the page doesn't grow vertically with every new metric. */
function RunTrends({ points }: { points: ITrendPoint[] }) {
  const [doctrineAxis, setDoctrineAxis] = useState<DoctrineAxisKey>('prioritization');
  if (points.length < 2) return null;
  const doctrineAxisLabel =
    doctrineAxes.find((axis) => axis.key === doctrineAxis)?.label ?? doctrineAxis;

  const panels: ITrendPanelSpec[] = [
    {
      title: 'Quality',
      domain: { min: 0, max: 1 },
      formatY: (v) => `${Math.round(v * 100)}%`,
      series: [
        {
          key: 'passRate',
          label: 'events passed',
          color: 'var(--horizon-glow)',
          values: points.map((p) => p.passRate),
        },
        {
          key: 'resolvedRate',
          label: 'resolved',
          color: 'var(--status-info)',
          values: points.map((p) => p.resolvedRate),
        },
        {
          key: 'verdictAccuracy',
          label: 'verdict acc.',
          color: 'var(--status-success)',
          values: points.map((p) => p.verdictAccuracy),
        },
      ],
    },
    {
      title: 'Latency',
      formatY: (v) => `${Math.round(v)}s`,
      series: [
        {
          key: 'meanResponse',
          label: 'response',
          color: 'var(--horizon-glow)',
          values: points.map((p) => p.meanResponseSec),
        },
        {
          key: 'meanFirstTouch',
          label: 'first touch',
          color: 'var(--status-info)',
          values: points.map((p) => p.meanFirstTouchSec),
        },
      ],
    },
    {
      title: 'Cost',
      formatY: (v) => `$${v < 0.01 ? v.toFixed(4) : v.toFixed(3)}`,
      series: [
        {
          key: 'costUsd',
          label: 'LLM $',
          color: 'var(--horizon-glow)',
          values: points.map((p) => p.costUsd),
        },
      ],
    },
    // Strategy-judge doctrine (PR #965): five 1–5 axes; one axis at a time via
    // the selector so the panel keeps a single labeled series.
    {
      title: 'Doctrine',
      domain: { min: 1, max: 5 },
      formatY: (v) => `${Math.round(v * 10) / 10}/5`,
      series: [
        {
          key: `doctrine-${doctrineAxis}`,
          label: doctrineAxisLabel.toLowerCase(),
          color: 'var(--status-success)',
          values: points.map((p) => p.doctrine[doctrineAxis]),
        },
      ],
    },
  ];

  const live = panels.filter((p) => hasSeriesData(p.series));
  if (live.length === 0) return null;

  const doctrineLive = hasSeriesData(
    doctrineAxes.map((axis) => ({
      key: axis.key,
      label: axis.label,
      color: '',
      values: points.map((p) => p.doctrine[axis.key]),
    })),
  );

  return (
    <section className="card">
      <div
        className="card-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <h3 className="t-h3">Run trends</h3>
        {doctrineLive && (
          <label
            className="t-sm t-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}
          >
            Doctrine axis
            <select
              className="select-native"
              value={doctrineAxis}
              onChange={(e) => setDoctrineAxis(e.target.value as DoctrineAxisKey)}
              aria-label="Doctrine axis to trend"
            >
              {doctrineAxes.map((axis) => (
                <option key={axis.key} value={axis.key}>
                  {axis.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div
        className="card-body"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 'var(--sp-3)',
          alignItems: 'start',
        }}
      >
        {live.map((panel) => (
          <TrendPanel
            key={panel.title}
            title={panel.title}
            points={points}
            domain={panel.domain}
            formatY={panel.formatY}
            series={panel.series}
          />
        ))}
      </div>
    </section>
  );
}
function BenchmarkRunsPage() {
  const { benchmarkId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<BenchmarkRun | null>(null);
  const [downloadError, setDownloadError] = useState<{ runId: string; message: string } | null>(
    null,
  );
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  /** Classical baseline confirm target — separate from official LLM Start on the list page. */
  const [classicalTarget, setClassicalTarget] = useState<ClassicalStack | null>(null);
  const [replayTarget, setReplayTarget] = useState<BenchmarkRun | null>(null);

  const benchmarkQuery = useQuery({
    queryKey: ['benchmarks', benchmarkId],
    queryFn: () => client.http.get(`v1/benchmarks/${benchmarkId}`).json<Benchmark>(),
  });
  const runsQuery = useQuery({
    queryKey: ['benchmarks', benchmarkId, 'runs'],
    queryFn: () => client.http.get(`v1/benchmarks/${benchmarkId}/runs`).json<BenchmarkRun[]>(),
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'PREPARING' || run.status === 'RUNNING')
        ? 2000
        : false,
  });

  const deleteRun = useMutation({
    mutationFn: (runId: string) =>
      client.http.delete(`v1/benchmarks/${benchmarkId}/runs/${runId}`).text(),
    onSuccess: () => {
      setDeleteTarget(null);
      fork(qc.invalidateQueries({ queryKey: ['benchmarks', benchmarkId, 'runs'] }));
    },
  });
  const updateLabel = useMutation({
    mutationFn: ({ runId, label }: { runId: string; label: string | null }) =>
      client.http
        .patch(`v1/benchmarks/${benchmarkId}/runs/${runId}`, { json: { label } })
        .json<BenchmarkRun>(),
    onSuccess: () => {
      setEditingLabelId(null);
      fork(qc.invalidateQueries({ queryKey: ['benchmarks', benchmarkId, 'runs'] }));
      fork(qc.invalidateQueries({ queryKey: ['benchmarks'] }));
    },
  });
  const startClassical = useMutation({
    mutationFn: (stack: ClassicalStack) =>
      client.http
        .post(`v1/benchmarks/${benchmarkId}/start`, { json: buildClassicalStartBody(stack) })
        .json(),
    onSuccess: () => {
      setClassicalTarget(null);
      fork(qc.invalidateQueries({ queryKey: ['benchmarks', benchmarkId] }));
      fork(qc.invalidateQueries({ queryKey: ['benchmarks', benchmarkId, 'runs'] }));
      fork(qc.invalidateQueries({ queryKey: ['benchmarks'] }));
    },
  });

  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);
  const armIds = useMemo(() => distinctArmIds(runs), [runs]);
  const stackIds = useMemo(() => distinctStacks(runs), [runs]);
  const [armFilter, setArmFilter] = useState<string>('all');
  const [stackFilter, setStackFilter] = useState<string>('all');
  // One filter drives KPIs, trends, and history so the page tells one story:
  // comparing runs within an arm/stack is the only apples-to-apples read.
  const visibleRuns = useMemo(
    () =>
      runs.filter(
        (run) =>
          (armFilter === 'all' || run.armId === armFilter) &&
          (stackFilter === 'all' || (run.stack ?? defaultStack) === stackFilter),
      ),
    [runs, armFilter, stackFilter],
  );
  const trendPoints = useMemo(() => buildTrendPoints(visibleRuns), [visibleRuns]);
  // Columns that are dashes on every visible row are dropped entirely —
  // model/agents only exist on post-#965 runs, kit metrics only on kit runs.
  const showModelCol = visibleRuns.some((run) => runModelLabel(run) !== null);
  const showAgentsCol = visibleRuns.some((run) => runAgentsUsed(run) != null);
  const showShapeCol = visibleRuns.some((run) => runTopologyShape(run) !== null);
  const showKitCol = visibleRuns.some((run) => (run.kitMetrics?.length ?? 0) > 0);
  // When multiple stacks are in view with no stack filter, the blended KPI
  // headline would average unlike cohorts (e.g. a $0 classical run with a
  // $7 LLM run) — dishonest. In that case the Head-to-head verdict is the
  // headline instead of the KPI cards.
  const blendingStacks = stackFilter === 'all' && stackIds.length > 1;
  const toggleExpanded = (runId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  // KPI headline over the visible (arm-filtered) slice — latest completed run
  // + averages, mirroring the evals entity page's stat cards.
  const kpiCards = useMemo<IBenchKpiCard[]>(() => {
    const finished = visibleRuns.filter(
      (run) => run.status !== 'PREPARING' && run.status !== 'RUNNING',
    );
    const scored = finished
      .map((run) => ({ run, score: scoreRun(run) }))
      .filter((x) => x.score.scorable > 0);
    const latest = scored[0] ?? null;
    const rates = scored.map((x) => x.score.passRate).filter((r): r is number => r !== null);
    const avgRate = rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
    const costs = scored.map((x) => x.score.costUsd).filter((c): c is number => c !== null);
    const avgCost = costs.length > 0 ? costs.reduce((a, b) => a + b, 0) / costs.length : null;
    const cards: IBenchKpiCard[] = [
      {
        label: 'Latest score',
        value: latest?.score.passRate != null ? `${Math.round(latest.score.passRate * 100)}%` : '—',
        sub: latest
          ? `${latest.score.passed} / ${latest.score.scorable} events passed`
          : 'no scored runs yet',
        color: passRateColor(latest?.score.passRate ?? null),
      },
      {
        label: 'Avg pass rate',
        value: avgRate != null ? `${Math.round(avgRate * 100)}%` : '—',
        sub: `across ${scored.length} scored run${scored.length === 1 ? '' : 's'}`,
        color: passRateColor(avgRate),
      },
      {
        label: 'Total runs',
        value: String(runs.length),
        sub:
          armIds.length > 0
            ? `${armIds.length} experiment arm${armIds.length === 1 ? '' : 's'}`
            : 'all time',
      },
      {
        label: 'Latest cost',
        value: latest?.score.costUsd != null ? formatRunCost(latest.score.costUsd) : '—',
        sub: avgCost != null ? `avg ${formatRunCost(avgCost)}` : 'LLM spend per run',
      },
    ];
    return cards;
  }, [visibleRuns, runs.length, armIds.length]);

  const kit = benchmarkQuery.data?.kit;
  const hasKit = (kit?.watches?.length ?? 0) > 0 || Boolean(kit?.scorecard);

  return (
    <div style={stack3}>
      <nav aria-label="Breadcrumb" className="bc">
        <Link to={routes.ORCHESTRATION_BENCHMARKS}>Benchmarks</Link>
        <span className="sep" aria-hidden="true">
          ›
        </span>
        <span className="item is-leaf">{benchmarkQuery.data?.name ?? 'Benchmark'}</span>
      </nav>
      <PageHeader
        title={benchmarkQuery.data?.name ?? 'Benchmark'}
        actions={<BackButton to={routes.ORCHESTRATION_BENCHMARKS} label="Benchmarks" />}
      />

      {benchmarkQuery.isLoading && <Spinner />}
      {benchmarkQuery.error && (
        <div className="alert alert--danger">
          Failed to load: {(benchmarkQuery.error as Error).message}
        </div>
      )}
      {deleteRun.error && (
        <div className="alert alert--danger">{(deleteRun.error as Error).message}</div>
      )}
      {/* Blended KPIs are only honest within ONE stack; across stacks the
          Head-to-head verdict below is the headline instead. */}
      {!blendingStacks && <BenchKpiCards cards={kpiCards} />}

      {benchmarkQuery.data && (
        <section className="card">
          <div
            className="card-body"
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}
          >
            <SectionLabel>Kit (watches + scorecard)</SectionLabel>
            {!hasKit ? (
              <p className="t-sm t-secondary" style={{ margin: 0 }}>
                No kit — trials launch without watches or a scorecard (driver score only, if any).
              </p>
            ) : (
              <>
                {(kit?.watches ?? []).map((w) => (
                  <div key={w.name} className="t-sm">
                    <strong>{w.name}</strong> · {w.toolName}
                    {w.selector ? ` · sel ${w.selector}` : ''}
                    {w.laneId ? ` · lane ${w.laneId}` : ''} · {w.wakeDelivery} · {w.intervalSec}s
                  </div>
                ))}
                {kit?.scorecard && (
                  <div className="t-sm">
                    <strong>Scorecard:</strong>{' '}
                    {kit.scorecard.objective ?? `${kit.scorecard.metrics.length} metric(s)`}
                    {kit.scorecard.drive ? ' · drive on' : ''}
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      {kit.scorecard.metrics.map((m) => (
                        <li key={m.id} className="t-xs t-muted">
                          {m.label ?? m.id}: {m.selector} {m.comparator} {m.target}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {(armIds.length > 0 || stackIds.length > 1) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-4)',
            flexWrap: 'wrap',
          }}
        >
          {stackIds.length > 1 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <SectionLabel>Stack</SectionLabel>
              <select
                className="select-native"
                value={stackFilter}
                onChange={(e) => setStackFilter(e.target.value)}
                aria-label="Filter runs by agent stack"
              >
                <option value="all">All stacks</option>
                {stackIds.map((id) => (
                  <option key={id} value={id}>
                    {stackDisplayName(id)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {armIds.length > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <SectionLabel>Arm</SectionLabel>
              <select
                className="select-native"
                value={armFilter}
                onChange={(e) => setArmFilter(e.target.value)}
                aria-label="Filter runs by experiment arm"
              >
                <option value="all">All arms</option>
                {armIds.map((armId) => (
                  <option key={armId} value={armId}>
                    {armId}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {/* The verdict — who won and whether the gap clears the noise — is the
          headline evidence, so it sits directly under the filters. It
          subsumes the old naive "Stack comparison" panel (which crowned an
          N=1 "leader" on a blended average); the per-cohort mean±σ + the
          significance banner are the honest version of the same question. */}
      <ReliabilitySection
        benchmarkId={benchmarkId}
        armFilter={armFilter}
        stackFilter={stackFilter}
        pollActive={runs.some((run) => run.status === 'PREPARING' || run.status === 'RUNNING')}
      />

      {/* Classical baselines: same Start endpoint, stack=classical-*. Wipes the
          group's threads (API) but skips threadStartupConfig — no LLM PAs. */}
      {benchmarkQuery.data && (
        <section className="card">
          <div className="card-header">
            <h3 className="t-h3">Classical baselines</h3>
          </div>
          <div className="card-body" style={stack3}>
            <p className="t-sm t-secondary" style={{ margin: 0 }}>
              Inline convenience — not the official &ldquo;Start benchmark&rdquo; on the list page.
              Clears every thread in this orchestration group, then runs the non-LLM dispatcher on
              the scenario. Does <strong>not</strong> launch configured trial threads (no Non Code /
              code-mode agent), even when this bench is set up for them.
            </p>
            {!benchmarkQuery.data.remoteBenchmarkKey?.trim() ? (
              <p className="t-sm t-muted" style={{ margin: 0 }}>
                Needs a driver Scenario ID on the benchmark (Edit on the list page) before classical
                arms can start.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {classicalArmPresets.map((preset) => {
                  const busy = runs.some(
                    (run) => run.status === 'PREPARING' || run.status === 'RUNNING',
                  );
                  return (
                    <button
                      key={preset.stack}
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={busy || startClassical.isPending}
                      title={
                        busy
                          ? 'A run is already in progress on this benchmark'
                          : `Start ${stackDisplayName(preset.stack)} (no LLM threads)`
                      }
                      onClick={() => setClassicalTarget(preset.stack)}
                    >
                      {preset.shortLabel}
                    </button>
                  );
                })}
              </div>
            )}
            {startClassical.error && (
              <div className="alert alert--danger">{(startClassical.error as Error).message}</div>
            )}
          </div>
        </section>
      )}

      <RunTrends points={trendPoints} />

      <section className="card">
        <div className="card-header">
          <h3 className="t-h3">Run history</h3>
        </div>
        <div className="card-body" style={stack3}>
          {runsQuery.isLoading && <Spinner inline={true} />}
          {runs.length === 0 && !runsQuery.isLoading && (
            <EmptyState
              title="No runs yet"
              description="Start an LLM run from the benchmarks list, or use Classical baselines above on this page."
            />
          )}
          {runs.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ ...benchThStyle, width: 28 }} />
                    <th style={benchThStyle}>Started</th>
                    <th style={benchThStyle}>Stack</th>
                    <th style={benchThStyle}>Label</th>
                    <th style={benchThStyle}>Arm</th>
                    {showModelCol && <th style={benchThStyle}>Model</th>}
                    {showAgentsCol && <th style={benchThStyle}>Agents</th>}
                    {showShapeCol && <th style={benchThStyle}>Shape</th>}
                    <th style={benchThStyle}>Status</th>
                    <th style={benchThStyle}>Score</th>
                    <th style={benchThStyle}>Cost</th>
                    <th style={benchThStyle}>Duration</th>
                    {showKitCol && <th style={benchThStyle}>Kit metric</th>}
                    <th style={{ ...benchThStyle, width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {visibleRuns.map((run, runIndex) => {
                    const active = run.status === 'PREPARING' || run.status === 'RUNNING';
                    const score = scoreRun(run);
                    // Runs are newest-first: the next VISIBLE row is this run's
                    // baseline — when filtered to one arm, that's the previous
                    // trial of the same arm (the honest comparison).
                    const olderRun = visibleRuns[runIndex + 1];
                    const isExpanded = expanded.has(run.id);
                    const armChip = armBadgeLabel(run);
                    return (
                      <Fragment key={run.id}>
                        <tr
                          onClick={() => toggleExpanded(run.id)}
                          style={{
                            borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
                            cursor: 'pointer',
                          }}
                        >
                          <td style={benchTdStyle} aria-hidden={true}>
                            <span
                              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for run started ${formatDateTime(run.startedAt)}`}
                              style={{ display: 'inline-block', width: 12 }}
                            >
                              {isExpanded ? '▾' : '▸'}
                            </span>
                          </td>
                          <td style={{ ...benchTdStyle, whiteSpace: 'nowrap' }} className="t-sm">
                            {formatDateTime(run.startedAt)}
                          </td>
                          <td style={benchTdStyle}>
                            <span
                              className="badge badge--secondary"
                              style={truncate}
                              title={`Ran on ${stackDisplayName(run.stack)}`}
                            >
                              {stackDisplayName(run.stack)}
                            </span>
                            {run.stack && run.stack !== defaultStack && (
                              <div
                                className="t-secondary"
                                style={{ fontSize: 10, fontStyle: 'italic', marginTop: 2 }}
                                title="Imported from an external harness; score computed server-side, but the run log is self-reported"
                              >
                                self-reported
                              </div>
                            )}
                          </td>
                          <td style={benchTdStyle} onClick={(e) => e.stopPropagation()}>
                            {editingLabelId === run.id ? (
                              <input
                                className="input"
                                style={{ padding: '2px 6px', minWidth: 0, width: '100%' }}
                                value={labelDraft}
                                autoFocus={true}
                                maxLength={120}
                                disabled={updateLabel.isPending}
                                onChange={(e) => setLabelDraft(e.target.value)}
                                onBlur={() => {
                                  const next = labelDraft.trim();
                                  const prev = run.label ?? '';
                                  if (next === prev) {
                                    setEditingLabelId(null);
                                    return;
                                  }
                                  updateLabel.mutate({
                                    runId: run.id,
                                    label: next === '' ? null : next,
                                  });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                  if (e.key === 'Escape') setEditingLabelId(null);
                                }}
                                placeholder="label this run"
                              />
                            ) : (
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                style={{ padding: '2px 6px', maxWidth: '100%' }}
                                title={
                                  run.label
                                    ? `${run.label} — click to edit`
                                    : 'Click to add a run label'
                                }
                                onClick={() => {
                                  setEditingLabelId(run.id);
                                  setLabelDraft(run.label ?? '');
                                }}
                              >
                                {run.label ? (
                                  <span className="badge badge--neutral" style={truncate}>
                                    {run.label}
                                  </span>
                                ) : (
                                  <span className="t-secondary">+ label</span>
                                )}
                              </button>
                            )}
                          </td>
                          <td style={benchTdStyle}>
                            {armChip ? (
                              <span
                                className="badge badge--info"
                                style={truncate}
                                title={`Experiment arm ${run.armId}`}
                              >
                                {armChip}
                              </span>
                            ) : (
                              <span className="t-secondary">—</span>
                            )}
                          </td>
                          {showModelCol && (
                            <td style={benchTdStyle} title={runModelTitle(run)}>
                              {runModelLabel(run) ? (
                                <span className="badge badge--neutral" style={truncate}>
                                  {runModelLabel(run)}
                                </span>
                              ) : (
                                <span className="t-secondary">—</span>
                              )}
                            </td>
                          )}
                          {showAgentsCol && (
                            <td style={benchTdStyle} title={runAgentsTitle(run)}>
                              {runAgentsUsed(run) != null ? (
                                <span className="badge badge--neutral" style={truncate}>
                                  {runAgentsLabel(run)}
                                </span>
                              ) : (
                                <span className="t-secondary">—</span>
                              )}
                            </td>
                          )}
                          {showShapeCol && (
                            <td style={benchTdStyle}>
                              {runTopologyShape(run) ? (
                                <span
                                  className="badge badge--outline"
                                  style={truncate}
                                  title="How the stack organized itself (coordination topology)"
                                >
                                  {runTopologyShape(run)}
                                </span>
                              ) : (
                                <span className="t-secondary">—</span>
                              )}
                            </td>
                          )}
                          <td style={benchTdStyle}>
                            <span className={statusBadge(run.status)}>
                              {run.status.toLowerCase()}
                            </span>
                          </td>
                          <td
                            style={{
                              ...benchTdStyle,
                              fontFamily: 'var(--font-mono)',
                              whiteSpace: 'nowrap',
                            }}
                            className="t-sm"
                          >
                            {score.scorable > 0 ? (
                              <span style={{ color: passRateColor(score.passRate) }}>
                                {score.passed}/{score.scorable}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td
                            style={{
                              ...benchTdStyle,
                              fontFamily: 'var(--font-mono)',
                              whiteSpace: 'nowrap',
                            }}
                            className="t-sm"
                          >
                            {formatRunCost(score.costUsd)}
                          </td>
                          <td
                            style={{ ...benchTdStyle, whiteSpace: 'nowrap' }}
                            className="t-sm t-secondary"
                          >
                            {formatDuration(run.startedAt, run.finishedAt)}
                          </td>
                          {showKitCol && (
                            <td style={benchTdStyle} className="t-sm t-secondary">
                              <span
                                style={{ ...truncate, maxWidth: 220 }}
                                title={formatKitMetricsSummary(run.kitMetrics)}
                              >
                                {formatKitMetricsSummary(run.kitMetrics)}
                              </span>
                            </td>
                          )}
                          <td
                            style={{ ...benchTdStyle, whiteSpace: 'nowrap' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="btn btn--ghost btn--sm"
                              disabled={active || deleteRun.isPending}
                              onClick={() => setDeleteTarget(run)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: 'var(--surface-2)' }}>
                            <td
                              colSpan={
                                // base columns incl. the always-on Stack column
                                10 +
                                (showModelCol ? 1 : 0) +
                                (showAgentsCol ? 1 : 0) +
                                (showShapeCol ? 1 : 0) +
                                (showKitCol ? 1 : 0)
                              }
                              style={{ padding: 'var(--sp-3)' }}
                            >
                              <div style={stack3}>
                                {run.resolvedProfile?.baselineDrift && (
                                  <div className="alert alert--warning" style={{ margin: 0 }}>
                                    <strong>Baseline drift</strong> — a definition changed since
                                    this arm&apos;s first run
                                    {run.resolvedProfile.baselineDriftFromRunId
                                      ? ` (${run.resolvedProfile.baselineDriftFromRunId.slice(0, 8)}…)`
                                      : ''}
                                    ; comparisons against earlier trials may be off.
                                    {(run.resolvedProfile.baselineDriftSummary?.length ?? 0) >
                                      0 && (
                                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                                        {run.resolvedProfile.baselineDriftSummary?.map((line) => (
                                          <li key={line} className="t-xs">
                                            {line}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                )}
                                {run.resolvedProfile && (
                                  <p className="t-sm t-secondary" style={{ margin: 0 }}>
                                    <strong>What ran:</strong>{' '}
                                    {formatResolvedProfileSummary(run.resolvedProfile)}
                                  </p>
                                )}
                                {run.resolvedProfile?.profile.disabledTools?.length ? (
                                  <p
                                    className="t-sm t-secondary"
                                    style={{
                                      margin: 0,
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 'var(--sp-2)',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <strong>Tools withheld:</strong>{' '}
                                    {run.resolvedProfile?.profile.disabledTools?.map((id) => {
                                      const { label, qualifier } = formatDisabledToolId(id);
                                      return (
                                        <span
                                          key={id}
                                          className="badge badge--neutral"
                                          style={truncate}
                                          title={id}
                                        >
                                          {label} <span className="t-muted">{qualifier}</span>
                                        </span>
                                      );
                                    })}
                                  </p>
                                ) : null}
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 'var(--sp-2)',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <SectionLabel>Downloads</SectionLabel>
                                  {run.rawResult != null && (
                                    <RunDownloadButton
                                      http={client.http}
                                      path={`v1/benchmarks/${benchmarkId}/runs/${run.id}/dataset`}
                                      filename={`benchmark-run-${run.id.slice(0, 8)}-dataset.json`}
                                      label="Run dataset (JSON)"
                                      title="The raw driver dataset — for sim runs, the full scenario scorecard fetched at run end"
                                      onError={(message) =>
                                        setDownloadError(
                                          message ? { runId: run.id, message } : null,
                                        )
                                      }
                                    />
                                  )}
                                  {run.rawResult != null && (
                                    <button
                                      type="button"
                                      className="btn btn--ghost btn--sm"
                                      title="Birds-eye replay from the sim 10 Hz trajectory"
                                      onClick={() => setReplayTarget(run)}
                                    >
                                      Replay map
                                    </button>
                                  )}
                                  {run.launchedThreads.length > 0 && (
                                    <RunDownloadButton
                                      http={client.http}
                                      path={`v1/benchmarks/${benchmarkId}/runs/${run.id}/transcripts`}
                                      filename={`benchmark-run-${run.id.slice(0, 8)}-transcripts.txt`}
                                      label="Transcripts (.txt)"
                                      title="Full logs of every launched thread. Threads are cleared when the next run starts, but transcripts are archived at run end — archived runs download the snapshot captured at completion (kept for 7 days)."
                                      onError={(message) =>
                                        setDownloadError(
                                          message ? { runId: run.id, message } : null,
                                        )
                                      }
                                    />
                                  )}
                                  <RunDownloadButton
                                    http={client.http}
                                    path={`v1/benchmarks/${benchmarkId}/runs/${run.id}/manifest`}
                                    filename={`benchmark-run-${run.id.slice(0, 8)}-manifest.json`}
                                    label="Scoring manifest (JSON)"
                                    title="ScenarioRunManifest for the cross-stack scorer — pair it with the run dataset"
                                    onError={(message) =>
                                      setDownloadError(message ? { runId: run.id, message } : null)
                                    }
                                  />
                                </div>
                                {downloadError?.runId === run.id && (
                                  <div className="alert alert--danger" style={{ margin: 0 }}>
                                    {downloadError.message}
                                  </div>
                                )}
                                {run.results.length > 0 ? (
                                  <BenchmarkResults
                                    results={run.results}
                                    previous={olderRun?.results}
                                  />
                                ) : (
                                  <p className="t-sm t-secondary" style={{ margin: 0 }}>
                                    No driver results recorded for this run yet.
                                  </p>
                                )}
                                <ShiftReport value={run.strategyAnalysis} />
                                {(run.kitMetrics?.length ?? 0) > 0 && (
                                  <KitMetricsPanel metrics={run.kitMetrics} />
                                )}
                                {run.errorMessage && (
                                  <div className="alert alert--danger" style={{ margin: 0 }}>
                                    {run.errorMessage}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => {
          if (!deleteRun.isPending) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) deleteRun.mutate(deleteTarget.id);
        }}
        title="Delete run"
        message={`Delete this run's results (started ${
          deleteTarget ? formatDateTime(deleteTarget.startedAt) : ''
        })? This cannot be undone and does not affect other runs.`}
        confirmLabel={deleteRun.isPending ? 'Deleting…' : 'Delete'}
        destructive={true}
        pending={deleteRun.isPending}
      />

      <ConfirmDialog
        open={classicalTarget !== null}
        onClose={() => {
          if (!startClassical.isPending) setClassicalTarget(null);
        }}
        onConfirm={() => {
          if (classicalTarget) startClassical.mutate(classicalTarget);
        }}
        title={
          classicalTarget ? `Run ${stackDisplayName(classicalTarget)}` : 'Run classical baseline'
        }
        message={classicalTarget ? classicalStartConfirmDetail(classicalTarget) : ''}
        confirmLabel={startClassical.isPending ? 'Starting…' : 'Start classical run'}
        pending={startClassical.isPending}
      />

      {replayTarget ? (
        <RunReplayModal
          open={true}
          onClose={() => setReplayTarget(null)}
          benchmarkId={benchmarkId}
          run={replayTarget}
          siblings={runs}
        />
      ) : null}
    </div>
  );
}
